import React from "react";
import { CheckIcon, XIcon } from "lucide-react";
import { SectionCta } from "../SectionCta";
import { afterItems, beforeItems } from "../../data/content";
export function BeforeAfter() {
  return <section className="bg-white px-4 py-16 sm:px-6 sm:py-24" aria-labelledby="structured-title" data-id="element-81" data-mp="26ll08frx8f20">
      <div className="mx-auto max-w-3xl text-center" data-id="element-82" data-mp="22lkahyzdghfn">
        <p className="text-sm font-bold uppercase tracking-wide text-brand" data-id="element-83" data-mp="22ioeig7342tu">
          A more structured way to approach mandibular complete dentures
        </p>
        <h2 id="structured-title" className="mt-3 font-display text-[28px] font-black leading-tight text-ink sm:text-4xl" data-id="element-84" data-mp="2ud044czcj4fd">
          What If You Could Stop Guessing Why a Lower Denture Keeps Moving?
        </h2>
        <div className="mt-6 space-y-4 text-left text-[17px] leading-relaxed text-muted sm:text-center" data-id="element-85" data-mp="2laqwbuvgpp5o">
          <p data-id="element-86" data-mp="1ewrjmgxlwzrw">Lower complete dentures are among the most challenging areas of removable prosthodontics.</p>
          <p data-id="element-87" data-mp="19usizet94gyn">
            Unlike maxillary dentures, mandibular dentures must function within a smaller supporting area while
            interacting continuously with the tongue, cheeks, lips, and surrounding musculature.
          </p>
          <p data-id="element-88" data-mp="y4xz2y4esocx">
            And when patients present with severe ridge resorption, unfavorable anatomy, or compromised soft tissues,
            treatment planning becomes even more complex.
          </p>
        </div>
        <blockquote className="mt-8 border-l-4 border-gold-500 bg-cream px-5 py-5 text-left sm:px-7" data-id="element-89" data-mp="oemjgebd4qq7">
          <p className="text-lg font-semibold text-ink" data-id="element-90" data-mp="t5urp70yi61s">The real challenge isn't always knowing another technique.</p>
          <p className="mt-2 text-lg text-ink" data-id="element-91" data-mp="ffhkj6vw2vdi">
            It's knowing <strong data-id="element-92" data-mp="jfi93kcpcmya">what to evaluate</strong>, <strong data-id="element-93" data-mp="jgl1jwpoaxgb">which factors matter</strong>, and{" "}
            <strong data-id="element-94" data-mp="5gaaaskgbgum">how to organize your clinical decisions</strong> before proceeding.
          </p>
        </blockquote>
        <p className="mt-6 text-[17px] text-muted" data-id="element-95" data-mp="afd5llamq8pe">
          That's exactly what the Mandibular Complete Denture Blueprint™ is designed to help you do.
        </p>
      </div>

      <div className="mx-auto mt-14 grid max-w-6xl gap-5 md:grid-cols-2 md:gap-6" data-id="element-96" data-mp="27dsvszojl9vh">
        <div className="rounded-3xl bg-danger-soft p-6 ring-1 ring-danger/15 sm:p-8" data-id="element-97" data-mp="27bptpha47qae">
          <span className="inline-block rounded-lg bg-danger px-3 py-1 font-display text-sm font-black uppercase tracking-wide text-white" data-id="element-98" data-mp="239uwanvb1x1w">
            Before
          </span>
          <h3 className="mt-3 font-display text-xl font-extrabold text-ink sm:text-2xl" data-id="element-99" data-mp="2v2lj67r3m9eh">
            Without a structured clinical pathway
          </h3>
          <ul className="mt-6 space-y-4" data-id="element-100" data-mp="2m26wb3yhkm8v">
            {beforeItems.map(item => <li key={item} className="flex gap-3 text-base leading-snug text-ink" data-id="element-101" data-mp="2h3cd40wlwvo8">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-danger text-white" data-id="element-102" data-mp="2h5q7e7jgq3uq">
                  <XIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-103" data-mp="15ogs7x7d9xz8" />
                </span>
                {item}
              </li>)}
          </ul>
        </div>

        <div className="rounded-3xl bg-navy-900 p-6 text-white shadow-xl ring-1 ring-navy-700 sm:p-8" data-id="element-104" data-mp="10jcyfmcxpjwy">
          <span className="inline-block rounded-lg bg-gold-400 px-3 py-1 font-display text-sm font-black uppercase tracking-wide text-navy-950" data-id="element-105" data-mp="15ipvezxis7d9">
            After
          </span>
          <h3 className="mt-3 font-display text-xl font-extrabold sm:text-2xl" data-id="element-106" data-mp="1aazhg4vikna9">
            With the Mandibular Complete Denture Blueprint™
          </h3>
          <ul className="mt-6 space-y-4" data-id="element-107" data-mp="1j8t74wyrwiog">
            {afterItems.map(item => <li key={item} className="flex gap-3 text-base leading-snug text-white/95" data-id="element-108" data-mp="1o8o2pdthtwfu">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-white" data-id="element-109" data-mp="1t8k558mjlfta">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-110" data-mp="3q1lxhf6tyz3f" />
                </span>
                {item}
              </li>)}
          </ul>
        </div>
      </div>

      <p className="mx-auto mt-10 max-w-2xl text-center font-display text-xl font-extrabold leading-snug text-ink sm:text-2xl" data-id="element-111" data-mp="1p9salin5x3b1">
        The goal isn't to eliminate clinical complexity. It's to help you approach it{" "}
        <span className="text-brand" data-id="element-112" data-mp="1y9p1lqx0ye99">systematically.</span>
      </p>

      <SectionCta label="Yes! I want a clearer mandibular denture workflow" data-id="element-113" data-mp="1tdzyjdlsqf87" />
    </section>;
}